import React, { useState } from 'react';
import { ENDPOINTS } from 'features/daily-plan/constants';
import useDatatable, {
  buildRowActionsRendererDefinition,
  getColumnPreferences,
  getColumnsToRender
} from 'hooks/datatable';
import { rowClickBuilder } from 'hooks/profileActions';
import strings from 'strings';
import { FPIconButton } from 'UI/components/atoms';
import { When } from 'UI/components/atoms/When';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import { ChangeLogsContent } from 'UI/components/organisms/inventoryProfiles/ChangeLogsTab/ChangeLogsContent';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { buildCloseAndGoBackDefinition } from 'UI/components/templates/FPDrawer/FPDrawer.constants';
import {
  ScrollableDataTable,
  ScrollableDataTableLayout
} from 'UI/components/templates/ScrollableDataTableLayout';
import { RowActions } from 'UI/constants/defaults';
import { SvgArrowLeft } from 'UI/res';

import { COLUMNS, ORDER_BY_OPTIONS, PAGE_KEY } from './ChangeLogDrawer.constants';

const { taskDetails: COPIES } = strings.dailyPlan.drawer;

const getChangeLogEndpoint = (taskId, logId) => {
  return `${ENDPOINTS.changeLog
    .replace(':taskId', taskId)
    .replace(':id', logId)}?includes=vCreatedBy`;
};

export const ChangeLogDrawer = ({ onBack, onClose, taskId }) => {
  const [selectedLogId, setSelectedLogId] = useState(null);

  const initialPreferences = getColumnPreferences(PAGE_KEY, 0, ORDER_BY_OPTIONS, COLUMNS);
  const {
    columnPreferences,
    count,
    data,
    handleColumnDisplayChange,
    handleColumnOrderChange,
    handleColumnSortChange,
    handleFiltersToggle,
    handleKeywordChange,
    handlePageChange,
    handlePerPageChange,
    listState
  } = useDatatable({
    columnsDefinitions: COLUMNS,
    endpoint: ENDPOINTS.changeLogs.replace(':id', taskId),
    initialPreferences,
    key: PAGE_KEY,
    orderByOptions: ORDER_BY_OPTIONS,
    periodDefaultValue: null,
    sendDateWithTimezone: true,
    shouldScrollOnNavigate: true
  });

  const { isLoading, showWholeSkeleton, page, perPage, keyword } = listState;
  const { columns, columnOrder, orderBy, direction } = columnPreferences;

  const actions = [
    {
      name: RowActions.View,
      onClick: rowClickBuilder({
        columns,
        data,
        pickId: true,
        callback: id => setSelectedLogId(id)
      })
    }
  ];
  const actionsColumn = buildRowActionsRendererDefinition({
    actions,
    columns
  });

  const finalColumns = getColumnsToRender([...columns, actionsColumn]);

  const handleCloseDrawer = () => {
    setSelectedLogId(null);
  };

  const buttonDefinitions = buildCloseAndGoBackDefinition({ onClose, onGoBack: onBack });

  const changeLogDescriptionButtonDefinitions = buildCloseAndGoBackDefinition({
    onClose,
    onGoBack: handleCloseDrawer
  });

  return (
    <>
      <FPDrawer
        FPDrawerProps={{ open: true }}
        CardHeaderProps={{
          title: COPIES.changeLog.title,
          titleTypographyProps: {
            variant: 'h5'
          },
          avatar: (
            <FPIconButton
              onClick={onBack}
              iconMode="custom"
              icon={SvgArrowLeft}
              tooltipProps={{ title: 'Back' }}
            />
          )
        }}
        hasPaddingContent={false}
        onClose={onClose}
        footer={
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        }
      >
        <ScrollableDataTableLayout
          content={
            <ScrollableDataTable
              columnOrder={columnOrder?.length && columnOrder}
              columns={finalColumns}
              count={count ?? 0}
              data={data}
              draggableColumns={{
                enabled: true
              }}
              elevation={0}
              filter={false}
              loading={showWholeSkeleton}
              onColumnDisplayClick={handleColumnDisplayChange}
              onColumnOrderChange={handleColumnOrderChange}
              onColumnSortChange={handleColumnSortChange}
              onPageClick={handlePageChange}
              onPerPageClick={handlePerPageChange}
              onSearchTextChange={handleKeywordChange}
              onToggleFilters={handleFiltersToggle}
              page={page}
              refreshing={isLoading}
              rowsPerPage={perPage}
              searchText={keyword}
              selectableRows="none"
              selectToolbarPlacement="none"
              sortOrder={{ name: orderBy, direction }}
              title={strings.dailyPlan.emptyState.changeLogs.title}
            />
          }
        />
      </FPDrawer>

      <When condition={selectedLogId}>
        <FPDrawer
          CardHeaderProps={{
            title: strings.inventoryProfiles.sections.tabs.changeLogs.sectionName,
            titleTypographyProps: {
              variant: 'h5'
            },
            avatar: (
              <FPIconButton
                onClick={handleCloseDrawer}
                iconMode="custom"
                icon={SvgArrowLeft}
                tooltipProps={{ title: 'Back' }}
              />
            )
          }}
          FPDrawerProps={{ open: true }}
          onClose={onClose}
          footer={
            <FPButtonBuilder
              GridContainerProps={{ spacing: 2 }}
              buttonDefinitions={changeLogDescriptionButtonDefinitions}
            />
          }
        >
          <ChangeLogsContent endpoint={getChangeLogEndpoint(taskId, selectedLogId)} />
        </FPDrawer>
      </When>
    </>
  );
};
